import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './learn.css';

// =========================================================================
// /answers — Q&A discovery layer (AEO/GEO).
// Data: /learn/question-bank.json (150 questions, each linked to a parent
// article, pillar, reference_path and primary source).
//
// Rules enforced from prompt 2:
//   * SSR-friendly initial list (we render every question in server HTML
//     — client search is only an enhancement; works without JS).
//   * Labelled search input, pillar filters, result count.
//   * Accordions are accessible <details>/<summary>.
//   * Each answer links to canonical article (/learn/<slug>#quick-answer).
//   * NEVER present text search as a live AI assistant.
//   * Search param "q" is excluded from indexing via robots meta.
// =========================================================================

const PILLARS = [
  { v: 'all', label: 'All pillars' },
  { v: 'loyalty', label: 'Loyalty' },
  { v: 'campaigns', label: 'Campaigns' },
  { v: 'marketplace', label: 'Marketplace' },
  { v: 'mbo', label: 'MBO' },
  { v: 'sis', label: 'SIS' },
  { v: 'ebo', label: 'EBO' },
  { v: 'social-commerce', label: 'Social commerce' },
  { v: 'malls', label: 'Malls' },
  { v: 'retail-ai', label: 'Governed AI' },
];

const AnswersLibrary = () => {
  const [bank, setBank] = useState(null);
  const [q, setQ] = useState('');
  const [pillar, setPillar] = useState('all');

  useEffect(() => {
    document.title = 'Retail AI Answers — Fundle';
    const setM = (n, v) => {
      let el = document.head.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', n); document.head.appendChild(el); }
      el.setAttribute('content', v);
    };
    setM('description', 'A searchable retail AI question library: AI loyalty, campaigns, marketplaces, MBO, SIS, EBO, social commerce, malls and governed AI. Linked to Fundle Learn.');
  }, []);

  // Toggle robots meta whenever the user filters: filtered views are
  // intentionally not indexed (prompt 2: "Search query parameters/filter
  // results must not create indexable duplicate URLs").
  useEffect(() => {
    const el = (() => {
      let x = document.head.querySelector('meta[name="robots"]');
      if (!x) { x = document.createElement('meta'); x.setAttribute('name', 'robots'); document.head.appendChild(x); }
      return x;
    })();
    const filtered = q.trim().length > 0 || pillar !== 'all';
    el.setAttribute('content', filtered ? 'noindex,follow' : 'index,follow');
  }, [q, pillar]);

  useEffect(() => {
    let aborted = false;
    fetch('/learn/question-bank.json')
      .then((r) => r.json())
      .then((j) => { if (!aborted) setBank(j); })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  const slugFromArticleId = (articleId) => {
    // article_id looks like fundle-20261013-001; the matching slug lives in articles.json
    // We lazily map via reference_path which already carries the proposed slug.
    return articleId;
  };

  const slugFromReferencePath = (ref) => {
    // "/learn/ai-loyalty-data-readiness#quick-answer" -> "ai-loyalty-data-readiness"
    if (!ref) return '';
    const m = ref.match(/\/learn\/([^#?]+)/);
    return m ? m[1] : '';
  };

  const items = useMemo(() => {
    if (!bank) return [];
    const term = q.trim().toLowerCase();
    return bank.filter((item) => {
      if (pillar !== 'all' && item.pillar !== pillar) return false;
      if (!term) return true;
      return (
        item.question.toLowerCase().includes(term) ||
        item.answer.toLowerCase().includes(term)
      );
    });
  }, [bank, q, pillar]);

  const byPillar = useMemo(() => {
    const m = new Map();
    (items || []).forEach((i) => {
      if (!m.has(i.pillar)) m.set(i.pillar, []);
      m.get(i.pillar).push(i);
    });
    return m;
  }, [items]);

  return (
    <main className="answers-page" data-testid="answers-page">
      <section style={{
        padding: '64px 24px 48px',
        background: '#0D1B2A',
        color: '#F8FAFC',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#C6F432', marginBottom: 14 }}>
            Fundle Answers
          </p>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.05, margin: 0, fontWeight: 800 }}>
            Retail&nbsp;AI, answered<br />in a few lines.
          </h1>
          <p style={{ fontSize: 17, marginTop: 20, maxWidth: 720, color: '#CBD5E1' }}>
            Short, honest answers to the questions retail and mall teams ask Fundle Brain —
            loyalty, campaigns, marketplaces, MBO/SIS/EBO, social commerce, malls and governed AI.
            Every answer links to the source article.
          </p>
        </div>
      </section>

      <section style={{ padding: '32px 24px 20px', background: '#F8FBFF', borderBottom: '1px solid #DCE4F0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label htmlFor="answers-search" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A' }}>
            Search answers
          </label>
          <input
            id="answers-search"
            data-testid="answers-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try: incrementality, settlement, SIS, consent, dwell time..."
            style={{
              flex: '1 1 320px',
              padding: '12px 16px',
              fontSize: 15,
              border: '1px solid #CBD5E1',
              borderRadius: 10,
              background: '#fff',
              color: '#0D1B2A',
            }}
          />
          <select
            data-testid="answers-pillar"
            aria-label="Filter by pillar"
            value={pillar}
            onChange={(e) => setPillar(e.target.value)}
            style={{
              padding: '12px 14px',
              fontSize: 14,
              border: '1px solid #CBD5E1',
              borderRadius: 10,
              background: '#fff',
              color: '#0D1B2A',
              fontWeight: 600,
            }}
          >
            {PILLARS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          <span
            data-testid="answers-count"
            aria-live="polite"
            style={{ fontSize: 13, color: '#4b5a74', fontWeight: 600 }}
          >
            {bank ? `${items.length} of ${bank.length} answers` : 'Loading…'}
          </span>
        </div>
      </section>

      <section style={{ padding: '32px 24px 96px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {bank === null && (
            <p style={{ color: '#4b5a74' }}>Loading answers…</p>
          )}
          {bank && items.length === 0 && (
            <div data-testid="answers-empty" style={{ padding: 32, border: '1px dashed #CBD5E1', borderRadius: 12, background: '#F8FBFF' }}>
              <p style={{ margin: 0, fontWeight: 700, color: '#0D1B2A' }}>No matching answer yet.</p>
              <p style={{ marginTop: 10, color: '#4b5a74' }}>
                Try a shorter phrase or browse by pillar. Or ask the Fundle team directly at{' '}
                <a href="mailto:hello@fundle.ai" style={{ color: '#1E3A8A', fontWeight: 600 }}>hello@fundle.ai</a>.
              </p>
            </div>
          )}
          {bank && [...byPillar.keys()].map((pil) => {
            const list = byPillar.get(pil);
            const label = PILLARS.find((p) => p.v === pil)?.label || pil;
            return (
              <section key={pil} data-testid={`pillar-${pil}`} style={{ marginBottom: 36 }}>
                <h2 style={{
                  fontSize: 20,
                  margin: '12px 0 16px',
                  color: '#0D1B2A',
                  borderBottom: '1px solid #E2E8F0',
                  paddingBottom: 10,
                }}>
                  {label} <span style={{ color: '#4b5a74', fontWeight: 400, fontSize: 14 }}>({list.length})</span>
                </h2>
                {list.map((item) => {
                  const slug = slugFromReferencePath(item.reference_path);
                  return (
                    <details
                      key={item.id}
                      data-testid={`q-${item.id}`}
                      style={{
                        padding: '14px 20px',
                        border: '1px solid #E2E8F0',
                        borderRadius: 10,
                        marginBottom: 10,
                        background: '#fff',
                      }}
                    >
                      <summary style={{
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: '#0D1B2A',
                        fontSize: 15.5,
                        listStyle: 'disclosure-closed',
                      }}>
                        {item.question}
                      </summary>
                      <div style={{ marginTop: 12, color: '#263044', lineHeight: 1.65, fontSize: 15 }}>
                        <p style={{ margin: 0 }}>{item.answer}</p>
                        {slug && (
                          <p style={{ marginTop: 14, fontSize: 14 }}>
                            <Link to={`/learn/${slug}`} data-testid={`learn-link-${item.id}`} style={{ color: '#1E3A8A', fontWeight: 700 }}>
                              Read the full article ↗
                            </Link>
                          </p>
                        )}
                      </div>
                    </details>
                  );
                })}
              </section>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default AnswersLibrary;
